import React, { useCallback } from 'react';
import { FieldArrayRenderProps, useFieldArray } from 'react-final-form-arrays';

import { OfferFilter } from '@occc/graphql';
import { useChainId } from '@occc-app/Chain/hooks';

import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';
import { OfferActivationFormArrayItem } from './OfferActivationFormArrayItem';
import { offerActivationItemInitialValues } from './constants';

import { OfferActivationFormValues } from '../../types';
import { AddAnotherItem } from '../AddAnotherItem';
import { FormArrayItemSection } from '../FormArrayItemSection';

type OfferActivationFormContentProps = {
  name: string;
};

export const OfferActivationFormContent = React.memo(
  ({ name }: OfferActivationFormContentProps) => {
    const { fields } = useFieldArray<OfferActivationFormValues>(name, {
      subscription: { length: true },
    });
    const handleAddItem = useCallback(
      () => fields.push(offerActivationItemInitialValues),
      [fields],
    );
    const chainId = useChainId();
    const removeItem = useCallback((index: number) => fields.remove(index), [fields]);

    const { isEditable } = useCanEditFormFieldsContext();
    const isDeleteIconShown = isEditable && Boolean(fields.length && fields.length > 1);

    const makeOffersFilter = useCallback(
      (search: string): OfferFilter => {
        return {
          chainIdEquals: chainId,
          deleted: false,
          search: search || undefined,
          requiresActivationConfigured: true,
        };
      },
      [chainId],
    );

    const renderContent: Parameters<
      FieldArrayRenderProps<OfferActivationFormValues, any>['fields']['map']
    >[0] = useCallback(
      (fieldArrayName, index) => {
        return (
          <React.Fragment key={fieldArrayName}>
            <FormArrayItemSection
              index={index}
              title='Member has'
              isDeleteIconShown={isDeleteIconShown}
              onDeleteClick={removeItem}
            />
            <OfferActivationFormArrayItem
              fieldArrayName={fieldArrayName}
              makeOffersFilter={makeOffersFilter}
            />
          </React.Fragment>
        );
      },
      [makeOffersFilter, removeItem, isDeleteIconShown],
    );

    return (
      <>
        {fields.map(renderContent)}
        <AddAnotherItem title='Add Another Activation Condition' onClick={handleAddItem} />
      </>
    );
  },
);
